import { CheckIcon } from 'lucide-react';
import type {
  DocumentGenerationProgressDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';
import { cn } from '@/lib/utils';
import { useResearchStageSeen } from '../hooks/useResearchStageSeen';
import { buildGenerationProgress } from './generation-progress';
import '../components/web-research-activity.css';
import './document-canvas.css';

type GenerationProgressProps = {
  className?: string;
  /** `compact` : une seule ligne (bandeau du canvas) ; `full` : étapes détaillées (carte). */
  density?: 'compact' | 'full';
  generationId?: string;
  progress: DocumentGenerationProgressDTO | undefined;
  status?: DocumentGenerationStatusResponseDTO['status'];
};

/** Compteur « 3/8 » : rejoue la recette « Number pop-in » à chaque section terminée. */
function CounterPop({ done, total }: { done: number; total: number }) {
  return (
    <span
      className="t-digit-group is-animating inline-flex tabular-nums"
      key={done}
    >
      <span className="t-digit">{done}</span>
      <span>/{total}</span>
    </span>
  );
}

export function GenerationProgress({
  className,
  density = 'full',
  generationId,
  progress,
  status,
}: GenerationProgressProps) {
  const withResearch = useResearchStageSeen(generationId, progress);
  const view = buildGenerationProgress(progress, status, { withResearch });
  const isActive = view.percent < 100;

  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)} data-testid="generation-progress">
      {/* Seule l'étape courante est annoncée : le compteur change trop souvent pour un lecteur d'écran. */}
      <p className="sr-only" role="status">{view.currentLabel}</p>
      <div
        aria-label="Progression de la génération"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={view.percent}
        aria-valuetext={view.currentLabel}
        className="canvas-progress-track"
        role="progressbar"
      >
        <div
          className="canvas-progress-fill"
          data-active={isActive}
          style={{ width: `${Math.max(view.percent, 2)}%` }}
        />
      </div>
      <ol
        aria-hidden="true"
        className={cn(
          'flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs',
          density === 'compact' && 'gap-x-3 text-[11px]',
        )}
      >
        {view.steps.map((step) => {
          // Le compteur est rendu à part (animation) : le libellé de l'étape reste stable. Pendant la
          // recherche, le shimmer porte le libellé de chargement plutôt que le nom de l'étape.
          const label = step.state === 'active' && view.shimmerLabel ? view.shimmerLabel : step.label;
          return (
            <li
              className={cn(
                'canvas-step inline-flex items-center gap-1.5 transition-colors duration-300',
                step.state === 'active' && 'font-medium text-foreground',
                step.state === 'done' && 'text-muted-foreground',
                step.state === 'todo' && 'text-muted-foreground/55',
              )}
              data-state={step.state}
              key={step.key}
            >
              {step.state === 'done'
                ? <CheckIcon className="size-3 text-emerald-600 dark:text-emerald-400" />
                : <span className="canvas-step-dot" />}
              {step.state === 'active'
                ? <span className="t-shimmer" data-text={label}>{label}</span>
                : label}
              {step.key === 'write' && view.counter
                ? <CounterPop done={view.counter.done} total={view.counter.total} />
                : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
